/**
 * Normalize a CMS/nav link href to an internal SPA path.
 *
 * CMS content (footer nav, carousels, Lokalise strings) sometimes contains
 * site-relative hrefs without a leading slash (e.g. "shop-products/shop-by/color").
 * Left to the browser, those resolve relative to the CURRENT path, producing
 * doubled routes like "/shop-products/shop-by/shop-products/shop-by/color" that
 * then 404 (and trigger a bogus content lookup). Returning a leading-slash path
 * lets CmsContent route with the intended absolute path instead.
 *
 * Returns null for links that must NOT be treated as internal SPA routes:
 * empty, anchors (#...), external (http/https or protocol-relative //), or any
 * scheme link (mailto:, tel:, javascript:, ...). Those are left to the browser.
 */
export const toInternalPath = (href: string | null | undefined): string | null => {
  if (!href) { return null }
  const trimmed = href.trim()
  if (trimmed === '' || trimmed.startsWith('#') || trimmed.startsWith('//')) { return null }
  if (/^https?:\/\//i.test(trimmed)) { return null }
  // any explicit URL scheme (mailto:, tel:, javascript:, data:, ...)
  if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) { return null }
  return trimmed.startsWith('/') ? trimmed : `/${trimmed}`
}

/**
 * True when a content-lookup path looks malformed — a link-derived path that
 * never should have become a content route: an unescaped href fragment, an email
 * address, an escaped-quote/backslash string, or a literal null/undefined segment.
 *
 * Used to skip the backend content lookup, which would otherwise 404 and log a
 * Critical for every such path (~900/day/region across all cultures).
 */
export const isSuspectContentPath = (path: string | null | undefined): boolean => {
  if (!path) { return true }
  // characters that never appear in a legitimate content route
  if (/[@"\\]/.test(path)) { return true }
  // percent-encoded quote/backslash from stringified hrefs (%22 = ", %5C = \)
  if (/%22|%5c/i.test(path)) { return true }
  // an unresolved interpolation left a "null"/"undefined" segment
  if (/(^|\/)(null|undefined)(\/|$)/.test(path)) { return true }
  return false
}
